import { ENTRI_MESSAGE_ORIGINS, ENTRI_MESSAGE_PREFIX } from '../utils/constants';
import { logError } from '../utils/sentry-logger';

/**
 * @typedef {object} EntriMessageResult
 * @property {boolean} success - Whether the operation was successful
 * @property {string|null} domain - The purchased domain name (null if cancelled)
 * @property {string|null} provider - The domain provider (e.g., 'ionos')
 * @property {string|null} setupType - The type of setup (e.g., 'purchase', 'dns')
 * @property {string|null} lastStatus - The last status of the operation
 * @property {boolean} freeDomain - Whether this was a free domain offer
 * @property {boolean} cancelled - Whether the user cancelled the operation
 */

/**
 * @callback EntriMessageCallback
 * @param {EntriMessageResult} result - The parsed message result from Entri
 * @returns {void}
 */

/**
 * @typedef {object} IEntriMessageHandler
 * @property {function(EntriMessageCallback): string} subscribe - Subscribe to Entri messages
 * @property {function(string): boolean} unsubscribe - Unsubscribe using subscription ID
 * @property {function(): number} getSubscriberCount - Get count of active subscribers
 * @property {function(): void} reset - Clear all subscribers and reset state
 */

/**
 * Singleton service for handling Entri postMessage communication
 * Manages event listeners and distributes messages to subscribers
 *
 * This service provides a centralized way to handle all Entri communication,
 * supporting future features like DNS management, domain renewals, etc.
 *
 * @class EntriMessageHandler
 * @implements {IEntriMessageHandler}
 */
class EntriMessageHandler {
    constructor() {
        /**
         * @type {Map<string, Function>} Map of subscription IDs to callbacks
         */
        this.subscribers = new Map();

        /**
         * @type {number} Counter for generating unique subscription IDs
         */
        this.subscriptionIdCounter = 0;

        /**
         * @type {boolean} Whether the event listener is set up
         */
        this.isListening = false;

        this.handleMessage = this.handleMessage.bind(this);
    }

    /**
     * Sets up the global message event listener if not already listening
     * @private
     */
    setupListener() {
        if (!this.isListening) {
            window.addEventListener('message', this.handleMessage);
            this.isListening = true;
        }
    }

    /**
     * Removes the global message event listener if no subscribers remain
     * @private
     */
    teardownListener() {
        if (this.isListening && this.subscribers.size === 0) {
            window.removeEventListener('message', this.handleMessage);
            this.isListening = false;
        }
    }

    /**
     * Handles incoming postMessage events
     * @private
     * @param {MessageEvent} event - The message event from window
     */
    handleMessage(event) {
        // Validate origin
        if (!ENTRI_MESSAGE_ORIGINS.includes(event.origin)) {
            return;
        }

        // Check if it's an Entri message
        if (typeof event.data !== 'string' || !event.data.startsWith(ENTRI_MESSAGE_PREFIX)) {
            return;
        }

        try {
            // Parse the message
            const jsonString = event.data.substring(ENTRI_MESSAGE_PREFIX.length);
            const messageData = JSON.parse(jsonString);

            if (messageData.eventName !== 'onEntriClose') {
                return;
            }

            // Check if it's a close event (success or cancel)
            const result = {
                success: messageData.message?.success || false,
                domain: messageData.message?.domain || null,
                provider: messageData.message?.provider || null,
                setupType: messageData.message?.setupType || null,
                lastStatus: messageData.message?.lastStatus || null,
                freeDomain: messageData.message?.freeDomain || false,
                cancelled:
                    !messageData.message?.success &&
                    messageData.message?.lastStatus === 'IN_PROGRESS',
            };

            // Notify all subscribers
            // Create a copy of subscribers to iterate since callbacks might unsubscribe
            const subscribersCopy = Array.from(this.subscribers.entries());
            subscribersCopy.forEach(([id, callback]) => {
                try {
                    callback(result);
                } catch (error) {
                    // Log callback errors to Sentry but don't prevent unsubscribe
                    logError(error, {
                        tags: {
                            source: 'EntriMessageHandler',
                            action: 'subscriber_callback',
                            error_type: 'callback_error',
                        },
                        contexts: {
                            subscription_id: id,
                            message_success: result.success,
                            domain: result.domain,
                        },
                    });
                } finally {
                    // Auto-unsubscribe after delivering the message
                    // This prevents memory leaks and ensures each purchase flow
                    // only receives one message
                    this.unsubscribe(id);
                }
            });
        } catch (error) {
            // Log parsing errors to Sentry
            logError(error, {
                tags: {
                    source: 'EntriMessageHandler',
                    action: 'handleMessage',
                    error_type: 'parse_error',
                },
                contexts: {
                    message_data:
                        typeof event.data === 'string'
                            ? event.data.substring(0, 100)
                            : 'non-string',
                    origin: event.origin,
                },
            });
        }
    }

    /**
     * Subscribes a callback to receive Entri messages
     * The callback will be automatically unsubscribed after receiving a message
     *
     * @param {Function} callback - Function to call when a message is received
     * @returns {string} Subscription ID that can be used to unsubscribe
     */
    subscribe(callback) {
        if (typeof callback !== 'function') {
            throw new Error('Callback must be a function');
        }

        this.subscriptionIdCounter += 1;
        const subscriptionId = `entri_sub_${this.subscriptionIdCounter}`;
        this.subscribers.set(subscriptionId, callback);

        // Set up listener if this is the first subscriber
        this.setupListener();

        return subscriptionId;
    }

    /**
     * Unsubscribes a callback using its subscription ID
     *
     * @param {string} subscriptionId - The ID returned from subscribe()
     * @returns {boolean} True if unsubscribed successfully, false if ID not found
     */
    unsubscribe(subscriptionId) {
        const deleted = this.subscribers.delete(subscriptionId);

        // Tear down listener if no more subscribers
        this.teardownListener();

        return deleted;
    }

    /**
     * Gets the current number of active subscribers
     * Useful for testing and debugging
     *
     * @returns {number} Number of active subscribers
     */
    getSubscriberCount() {
        return this.subscribers.size;
    }

    /**
     * Clears all subscribers and removes the event listener
     * Useful for testing and cleanup
     */
    reset() {
        this.subscribers.clear();
        this.teardownListener();
        this.subscriptionIdCounter = 0;
    }
}

// Export as singleton
const entriMessageHandler = new EntriMessageHandler();
export default entriMessageHandler;
